<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAlert } from 'dashboard/composables';
import { getContactPageUrl } from 'dashboard/helper/inboxTroubleshootSteps';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import BillingPlanCard from 'dashboard/components-next/billing/BillingPlanCard.vue';
import BillingPeriodToggle from 'dashboard/components-next/billing/BillingPeriodToggle.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BillingAPI from 'dashboard/api/billing';
import { useKashierReturn } from 'dashboard/composables/useKashierReturn';
import {
  BILLING_PERIOD_LABEL_KEYS,
  BILLING_PERIOD_SUFFIX_KEYS,
  STANDARD_BILLING_PERIOD_DAYS,
} from 'dashboard/constants/billingPeriods';
import {
  catalogHasDuration,
  firstAvailablePeriodDays,
  planHasDuration,
  resolvePlanDuration,
} from 'dashboard/composables/useBillingPlanCardContent';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { handleKashierReturnIfNeeded } = useKashierReturn();
const { hostURL } = useConfig();

const getAgents = useMapGetter('agents/getAgents');
const getInboxes = useMapGetter('inboxes/getInboxes');
const agentsUsed = computed(() => getAgents.value?.length ?? 0);
const inboxesUsed = computed(() => getInboxes.value?.length ?? 0);

const contactPageUrl = computed(() => getContactPageUrl(hostURL));

const plans = ref([]);
const isLoading = ref(true);
const isSubmitting = ref(false);
const submittingPlanId = ref(null);
const voucherDraft = ref('');
const appliedVoucher = ref(null);
const isApplyingVoucher = ref(false);
const selectedPeriodDays = ref(30);

const availablePeriodDays = computed(() =>
  STANDARD_BILLING_PERIOD_DAYS.filter(days =>
    catalogHasDuration(plans.value, days)
  )
);

const showBillingPeriodToggle = computed(
  () => availablePeriodDays.value.length > 0
);

const isPlanBillingUnavailable = plan =>
  !planHasDuration(plan, selectedPeriodDays.value);

const periodUnavailableMessage = computed(() =>
  t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.PERIOD_UNAVAILABLE', {
    period: t(
      `GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.${BILLING_PERIOD_LABEL_KEYS[selectedPeriodDays.value]}`
    ),
  })
);

watch(availablePeriodDays, days => {
  if (!days.length) return;
  if (!days.includes(selectedPeriodDays.value)) {
    selectedPeriodDays.value = days[0];
  }
});

const billingSuffix = computed(() => {
  const key = BILLING_PERIOD_SUFFIX_KEYS[selectedPeriodDays.value];
  return key ? t(`GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.${key}`) : '';
});

const planDurationFor = plan =>
  resolvePlanDuration(plan, { days: selectedPeriodDays.value });

const isPlanSubmitting = planId =>
  isSubmitting.value && submittingPlanId.value === planId;

const loadCatalog = async () => {
  isLoading.value = true;
  try {
    const { data } = await BillingAPI.getPlans();
    plans.value = data?.payload || [];
    selectedPeriodDays.value = firstAvailablePeriodDays(plans.value);
  } catch {
    useAlert(t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const onSubscribe = async ({ plan, agentsCount, inboxesCount }) => {
  const duration = planDurationFor(plan);
  if (!duration?.id) {
    useAlert(t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR'));
    return;
  }

  isSubmitting.value = true;
  submittingPlanId.value = plan.id;
  try {
    const { data: checkoutData } = await BillingAPI.createCheckout({
      subscription_plan_id: plan.id,
      plan_duration_id: duration.id,
      agents_count: agentsCount,
      inboxes_count: inboxesCount,
      voucher_code: appliedVoucher.value?.code || undefined,
    });
    const invoiceId = checkoutData.invoice_id;
    if (!invoiceId) {
      useAlert(t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR'));
      return;
    }
    const billingReturnPath = router.resolve(
      accountScopedRoute('general_settings_billing')
    ).href;
    const billingReturnUrl = `${window.location.origin}${billingReturnPath}`;
    const { data: kashirData } = await BillingAPI.createKashirCheckout({
      invoice_id: invoiceId,
      return_url: billingReturnUrl,
      cancel_url: `${billingReturnUrl}?checkout_cancelled=1&invoice_id=${invoiceId}`,
    });
    if (kashirData.checkout_url) {
      window.location.href = kashirData.checkout_url;
    } else {
      useAlert(
        kashirData.error ||
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_UNAVAILABLE')
      );
      router.push(accountScopedRoute('general_settings_billing'));
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.CHECKOUT_ERROR')
    );
  } finally {
    isSubmitting.value = false;
    submittingPlanId.value = null;
  }
};

const onApplyVoucher = async () => {
  const code = voucherDraft.value.trim();
  if (!code) {
    appliedVoucher.value = null;
    return;
  }

  isApplyingVoucher.value = true;
  try {
    const { data } = await BillingAPI.validateVoucher(code);
    appliedVoucher.value = data?.payload || null;
    voucherDraft.value = appliedVoucher.value?.code || code;
    useAlert(
      t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.VOUCHER_APPLY_SUCCESS')
    );
  } catch (error) {
    appliedVoucher.value = null;
    useAlert(
      error?.response?.data?.error ||
        t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.VOUCHER_APPLY_ERROR')
    );
  } finally {
    isApplyingVoucher.value = false;
  }
};

onMounted(async () => {
  await handleKashierReturnIfNeeded();
  await loadCatalog();
});
</script>

<template>
  <div class="flex w-full min-w-0 max-w-full flex-col overflow-x-hidden">
    <BaseSettingsHeader
      :title="t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.TITLE')"
      title-icon="i-lucide-credit-card"
      show-back-button
      :back-url="accountScopedRoute('general_settings_billing')"
    />

    <div
      class="flex w-full min-w-0 flex-col gap-10 px-8 pb-2 pt-6 md:px-10 md:pt-8"
    >
      <div
        v-if="!isLoading"
        class="flex w-full min-w-0 flex-col gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-8"
      >
        <BillingPeriodToggle
          v-if="showBillingPeriodToggle"
          v-model="selectedPeriodDays"
          :available-days="availablePeriodDays"
        />
        <div
          class="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-end lg:max-w-md lg:shrink-0"
        >
          <Input
            v-model="voucherDraft"
            class="w-full min-w-0 sm:flex-1"
            :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.VOUCHER_CODE')"
            :placeholder="
              t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.VOUCHER_CODE_PLACEHOLDER')
            "
            :disabled="isApplyingVoucher"
            @keydown.enter.prevent="onApplyVoucher"
          />
          <Button
            size="sm"
            class="w-full shrink-0 sm:w-auto"
            :label="t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.APPLY_VOUCHER')"
            :is-loading="isApplyingVoucher"
            :disabled="isApplyingVoucher || !voucherDraft.trim()"
            @click="onApplyVoucher"
          />
        </div>
      </div>

      <div v-if="isLoading" class="text-sm text-n-slate-11">
        {{ t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.LOADING') }}
      </div>

      <div
        v-else
        class="grid w-full min-w-0 max-w-full grid-cols-1 gap-5 sm:grid-cols-[repeat(auto-fit,minmax(min(100%,17.5rem),1fr))]"
      >
        <div v-for="plan in plans" :key="plan.id" class="min-w-0 max-w-full">
          <BillingPlanCard
            :plan="plan"
            :duration="planDurationFor(plan)"
            :billing-suffix="billingSuffix"
            :billing-unavailable="isPlanBillingUnavailable(plan)"
            :billing-unavailable-message="periodUnavailableMessage"
            :applied-voucher="appliedVoucher"
            :agents-used="agentsUsed"
            :inboxes-used="inboxesUsed"
            :is-loading="isPlanSubmitting(plan.id)"
            :is-highlighted="plan.slug === 'growth'"
            @subscribe="onSubscribe"
          />
        </div>
      </div>

      <p v-if="!isLoading" class="mb-0 text-center text-sm text-n-slate-11">
        <span>{{
          t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.CONTACT_PREFIX')
        }}</span>
        {{ ' ' }}
        <a
          :href="contactPageUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="font-medium text-n-brand hover:underline"
        >
          {{ t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.CONTACT_LINK') }}
        </a>
        <span>{{
          t('GENERAL_SETTINGS.BILLING_SELECT_PLAN_PAGE.CONTACT_SUFFIX')
        }}</span>
      </p>
    </div>
  </div>
</template>
